import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import axiosInstance from "services/AxiosInstance.js";
import { notifySuccess, notifyError } from "../../components/CitoPlusToastr";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import { Formik } from "formik";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import * as Yup from "yup";

// Schema de validación con Yup fuera del componente
const validationSchema = Yup.object({
  tipo_concepto: Yup.string().required("El motivo es obligatorio"),
  descripcion: Yup.string().required("La descripción es obligatoria"),
  monto: Yup.number()
    .typeError("El monto debe ser un número")
    .positive("El monto debe ser mayor que cero")
    .required("El monto es obligatorio"),
  porcentajeImpuesto: Yup.number()
    .typeError("El porcentaje debe ser un número")
    .oneOf(
      [0, 5, 16, 19],
      "El porcentaje de impuesto debe ser uno de los valores permitidos: 0, 5, 16, 19",
    )
    .required("El porcentaje de impuesto es obligatorio"),
});

const ModalNotaCredito = ({ show, onHide, facturaId }) => {
  const [loading, setLoading] = useState(false);
  const [conImpuesto, setConImpuesto] = useState(true);
  const [tiposConcepto, setTiposConcepto] = useState([]);

  const initialValues = {
    motivo: "",
    descripcion: "",
    monto: "",
    porcentajeImpuesto: 19,
    baseGravable: "0.00",
    totalImpuesto: "0.00",
    total: "0.00",
  };

  useEffect(() => {
    if (show) {
      // Resetear el estado del checkbox al abrir el modal
      setConImpuesto(true);
      // Cargar los datos del dropdown de Tipo Concepto, que seria Motivo
      fetchTiposConcepto().then((data) => {
        console.log("Tipos concepto cargados:", data);
        setTiposConcepto(data);
      });
    }
  }, [show]);

  // Función para calcular los totales basados en los valores del formulario y el estado del checkbox
  const calcularTotales = (monto, porcentajeImpuesto, incluirImpuesto) => {
    const montoNum = parseFloat(monto) || 0;
    const porcentajeNum = parseFloat(porcentajeImpuesto) || 0;

    let baseGravable = 0;
    let totalImpuesto = 0;
    let total = 0;

    if (incluirImpuesto) {
      // El monto incluye impuesto, desglosar para obtener base gravable
      baseGravable = montoNum / (1 + porcentajeNum / 100);
      totalImpuesto = montoNum - baseGravable;
      total = montoNum;
    } else {
      // El monto es la base gravable, calcular impuesto
      baseGravable = montoNum;
      totalImpuesto = baseGravable * (porcentajeNum / 100);
      total = baseGravable + totalImpuesto;
    }

    return {
      baseGravable: baseGravable.toFixed(2),
      totalImpuesto: totalImpuesto.toFixed(2),
      total: total.toFixed(2),
    };
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);

    // Preparar los datos para enviar
    const dataToSend = {
      tipo_concepto: values.tipo_concepto,
      descripcion: values.descripcion,
      monto: parseFloat(values.monto),
      base_gravable: parseFloat(values.baseGravable),
      porcentaje_impuesto: parseFloat(values.porcentajeImpuesto),
      total_impuesto: parseFloat(values.totalImpuesto),
      total: parseFloat(values.total),
      conImpuesto: conImpuesto,
    };
    console.log("Datos a enviar:", dataToSend);

    try {
      const response = await axiosInstance.post(
        `/api/v3/parking/facturacion-electronica/${facturaId}/nota-de-credito/`,
        dataToSend,
      );
      if (response.status === 201 || response.status === 200) {
        resetForm();
        notifySuccess("Nota de crédito creada exitosamente");
        onHide();
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTiposConcepto = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/v3/parking/facturacion-electronica/dropdown-tipo-concepto/`,
      );
      console.log("Response data:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching tipos concepto:", error);
      handleErrors(error);
      return [];
    }
  };

  if (loading) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Crear Nota de Crédito</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center p-4">
            <SpinnerCitoPlus text="Creando nota de crédito..." />
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Crear Nota de Crédito</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            handleBlur,
            handleSubmit,
            touched,
            errors,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="tipo_concepto">
                    <Form.Label>Concepto</Form.Label>
                    <Form.Select
                      name="tipo_concepto"
                      value={values.tipo_concepto}
                      onChange={(e) => {
                        console.log("Concepto seleccionado:", e.target.value);
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      isInvalid={
                        touched.tipo_concepto && !!errors.tipo_concepto
                      }
                    >
                      <option value="">Seleccione un Tipo de Concepto</option>
                      {tiposConcepto.map((tipo) => (
                        <option
                          key={tipo.value || tipo.id}
                          value={tipo.value || tipo.id}
                        >
                          {tipo.label || tipo.name || tipo.descripcion}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.motivo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="descripcion">
                    <Form.Label>Descripción</Form.Label>
                    <Form.Control
                      type="text"
                      name="descripcion"
                      value={values.descripcion}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.descripcion && !!errors.descripcion}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.descripcion}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="monto">
                    <Form.Label>Monto</Form.Label>
                    <Form.Control
                      type="number"
                      name="monto"
                      value={values.monto}
                      onChange={(e) => {
                        handleChange(e);
                        const { baseGravable, totalImpuesto, total } =
                          calcularTotales(
                            e.target.value,
                            values.porcentajeImpuesto,
                            conImpuesto,
                          );
                        setFieldValue("baseGravable", baseGravable);
                        setFieldValue("totalImpuesto", totalImpuesto);
                        setFieldValue("total", total);
                      }}
                      onBlur={handleBlur}
                      isInvalid={touched.monto && !!errors.monto}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.monto}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="porcentajeImpuesto">
                    <Form.Label>% Impuesto</Form.Label>
                    <Form.Control
                      type="number"
                      name="porcentajeImpuesto"
                      value={values.porcentajeImpuesto}
                      onChange={(e) => {
                        handleChange(e);
                        const { baseGravable, totalImpuesto, total } =
                          calcularTotales(
                            values.monto,
                            e.target.value,
                            conImpuesto,
                          );
                        setFieldValue("baseGravable", baseGravable);
                        setFieldValue("totalImpuesto", totalImpuesto);
                        setFieldValue("total", total);
                      }}
                      onBlur={handleBlur}
                      isInvalid={
                        touched.porcentajeImpuesto &&
                        !!errors.porcentajeImpuesto
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.porcentajeImpuesto}
                    </Form.Control.Feedback>
                    <small className="text-muted">
                      <strong>Porcentajes válidos:</strong>
                      <br />• <strong>IVA:</strong> 0%, 5%, 16%, 19%
                    </small>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="conImpuesto"
                  label="El monto incluye impuesto"
                  checked={conImpuesto}
                  onChange={() => {
                    const newValue = !conImpuesto;
                    setConImpuesto(newValue);
                    const { baseGravable, totalImpuesto, total } =
                      calcularTotales(
                        values.monto,
                        values.porcentajeImpuesto,
                        newValue,
                      );
                    setFieldValue("baseGravable", baseGravable);
                    setFieldValue("totalImpuesto", totalImpuesto);
                    setFieldValue("total", total);
                  }}
                />
              </Form.Group>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group controlId="baseGravable">
                    <Form.Label>Base Gravable</Form.Label>
                    <Form.Control
                      type="text"
                      name="baseGravable"
                      value={values.baseGravable}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="totalImpuesto">
                    <Form.Label>Total Impuesto</Form.Label>
                    <Form.Control
                      type="text"
                      name="totalImpuesto"
                      value={values.totalImpuesto}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="total">
                    <Form.Label>Total</Form.Label>
                    <Form.Control
                      type="text"
                      name="total"
                      value={values.total}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="text-end mt-4">
                <Button
                  variant="danger"
                  onClick={onHide}
                  className="me-2"
                  size="sm"
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  size="sm"
                >
                  Crear Nota de Crédito
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default ModalNotaCredito;
